/**
 * Approve screen for a pending enrollment (renewal or portal request;
 * tracker item 85): dates, technician, auto-renew and the prices the visits
 * will carry. Approving creates the visit jobs. When the prices changed since
 * the quote was shown the server answers 409 RENEWAL_PRICE_CHANGED and this
 * screen shows old versus new and asks for confirmation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createAPIClient, IBundlesApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { getErrorCode, getErrorDetails, getErrorMessage } from '../../utils/apiError';
import { formatJobMoney } from '../../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { QuoteTable } from './RenewalQuoteDialog';

type ApproveApi = IBundlesApi & Pick<IUsersApi, 'listAssignableUsers'>;

/** The 409 details of RENEWAL_PRICE_CHANGED, or null for any other error. */
export function priceChangedDetails(err: unknown): types.RenewalPriceChangedDetails | null {
  if (getErrorCode(err) !== types.BUNDLE_ERROR_CODES.renewalPriceChanged) return null;
  const d = getErrorDetails(err);
  if (!d || typeof d.currentTotal !== 'string') return null;
  return {
    expectedTotal: typeof d.expectedTotal === 'string' ? d.expectedTotal : '',
    currentTotal: d.currentTotal,
    steps: Array.isArray(d.steps) ? (d.steps as types.RenewalPriceChangedDetails['steps']) : [],
  };
}

interface PanelProps {
  enrollment: types.BundleEnrollment;
  onApproved: (enrollment: types.BundleEnrollment) => void;
  onCancel: () => void;
}

export function ApproveEnrollmentPanel({ enrollment, onApproved, onCancel }: PanelProps) {
  const api = useMemo(() => createAPIClient() as ApproveApi, []);
  const [quote, setQuote] = useState<types.RenewalQuote | null>(null);
  const [users, setUsers] = useState<types.AssignableUser[]>([]);
  const [startDate, setStartDate] = useState(enrollment.startDate);
  const [assignedTo, setAssignedTo] = useState('');
  const [autoRenew, setAutoRenew] = useState(enrollment.autoRenew);
  const [changed, setChanged] = useState<types.RenewalPriceChangedDetails | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.allSettled([api.getEnrollmentQuote(enrollment.id), api.listAssignableUsers()]).then(([quoteResult, userResult]) => {
      if (!active) return;
      if (quoteResult.status === 'fulfilled') setQuote(quoteResult.value);
      else setError(getErrorMessage(quoteResult.reason, 'Could not load the quote'));
      if (userResult.status === 'fulfilled') setUsers(userResult.value);
    });
    return () => { active = false; };
  }, [api, enrollment.id]);

  const approve = async (confirm?: types.RenewalPriceChangedDetails) => {
    if (!quote || busy) return;
    setBusy(true);
    setError(null);
    try {
      const approved = await api.approveEnrollment(enrollment.id, {
        startDate,
        ...(assignedTo ? { assignedTo } : {}),
        autoRenew,
        // The pre-tax total the approver was shown; after a price change, the new one that was confirmed
        expectedTotal: confirm ? confirm.currentTotal : quote.subtotal,
        ...(confirm ? { confirmPriceChange: true } : {}),
      });
      onApproved(approved);
    } catch (err) {
      const details = priceChangedDetails(err);
      if (details) setChanged(details);
      else setError(getErrorMessage(err, 'Could not approve the enrollment'));
      setBusy(false);
    }
  };

  const reload = async () => {
    setChanged(null);
    setQuote(null);
    try { setQuote(await api.getEnrollmentQuote(enrollment.id)); } catch (err) { setError(getErrorMessage(err, 'Could not load the quote')); }
  };

  return (
    <section aria-labelledby="approve-heading" className="rounded-lg border border-amber-300 bg-white p-4 sm:p-6 shadow-sm">
      <h2 id="approve-heading" className="text-lg font-semibold text-gray-900">Approve {enrollment.bundleName}</h2>
      <p className="mt-1 text-sm text-gray-600">Approving creates every visit as its own job at the prices below, and fixes those prices.</p>
      {error && <Toast kind="error">{error}</Toast>}
      {changed && (
        <div role="alert" className="mt-4 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-900" data-testid="price-changed">
          <p className="font-semibold">The prices changed since you opened this quote.</p>
          <p className="mt-1">Total before tax was {formatJobMoney(changed.expectedTotal)}, now {formatJobMoney(changed.currentTotal)}.</p>
          {changed.steps.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {changed.steps.map((s) => <li key={s.step}>Visit {s.step}, {s.itemName}: {formatJobMoney(s.from)} → {formatJobMoney(s.to)}</li>)}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => approve(changed)}>Approve at {formatJobMoney(changed.currentTotal)}</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={reload}>Review the new quote</button>
          </div>
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="approve-start" className={labelClass}>First visit</label>
          <input id="approve-start" type="date" className={inputClass} value={startDate} disabled={busy} onChange={(e) => setStartDate(e.target.value)} />
          <p className="mt-1 text-xs text-gray-500">Changing it moves every visit by the same gaps.</p>
        </div>
        <div>
          <label htmlFor="approve-technician" className={labelClass}>Technician (all visits)</label>
          <select id="approve-technician" className={inputClass} value={assignedTo} disabled={busy} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">Unassigned (route default)</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-4 flex items-start gap-3">
        <input id="approve-autorenew" type="checkbox" className="mt-1 h-5 w-5" checked={autoRenew} disabled={busy} onChange={(e) => setAutoRenew(e.target.checked)} />
        <label htmlFor="approve-autorenew" className="font-medium text-gray-900">Auto-renew</label>
      </div>
      <div className="mt-4">
        {!quote && !error && <p role="status" className="text-sm text-gray-500">Loading the quote...</p>}
        {quote && <QuoteTable quote={quote} />}
      </div>
      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" className={secondaryButtonClass} onClick={onCancel} disabled={busy}>Close</button>
        <button type="button" className={primaryButtonClass} disabled={!quote || busy || Boolean(changed)} onClick={() => approve()}>{busy ? 'Approving...' : 'Approve'}</button>
      </div>
    </section>
  );
}
